import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface CustomerVehicle {
  plate: string;
  status: string;
  description?: string; // Descripción del vehículo (modelo, color, etc.)
  notes?: string; // Notas adicionales
}

export interface CustomerAbono {
  id?: string;
  vehicleId?: string;
  plate: string;
  planId: string;
  planName?: string;
  assignedSpaceId: string;
  spaceId?: string;
  spaceCode?: string;
  paidThroughDate?: string;
  graceDays: number;
    price: number;
  billingAnchorDate: string;
  nextInvoiceDate?: string;
  endOfMonthAligned: boolean;
  isDeleted?: boolean;
}

export interface SubscriptionProrationEstimate {
  subscriptionId: string;
  monthlyPrice: number;
  dailyRate: number;
  remainingDays: number;
  estimatedAmount: number;
  periodStart: string;
  periodEnd: string;
  currency: string;
}

export interface SubscriptionProrationEstimateResponse {
  success: boolean;
  code: string;
  reason: string;
  requestResult: SubscriptionProrationEstimate;
}

export interface TaxLineDto {
  taxId: string;
  taxName: string;
  taxRate: number;
  baseAmount: number;
  taxAmount: number;
  isIncluded: boolean;
}

export interface TaxBreakdownDto {
  subtotal: number;
  taxLines: TaxLineDto[];
  totalTaxes: number;
  total: number;
}

export interface CreateSubscriptionInvoiceRequest {
  subscriptionId: string;
  customerId: string;
  amount: number;
  taxBreakdown: TaxBreakdownDto | null;
  registerPayment: boolean;
  paymentMethod: string | null;
  amountReceived: number | null;
  cashBoxOpeningId: string | null;
}

export interface InvoiceDto {
  id: string;
  customerId: string;
  subscriptionId: string | null;
  invoiceNumber: string;
  issueDate: string;
  dueDate: string;
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  status: string;
  type: string;
  paymentRegistered: boolean;
  notes: string | null;
}

export interface InvoiceResponse {
  success: boolean;
  code: string;
  reason: string;
  requestResult: InvoiceDto;
}

export interface DebtAging {
  bucket0_30: number;
  bucket31_60: number;
  bucket61_90: number;
  bucket90Plus: number;
}

export interface DebtorRow {
  customerId: string;
  customerName: string;
  document: string;
  overdueAmount: number;
  totalDebt: number;
  oldestDueDate?: string;
  daysOverdue: number;
  lastPaymentDate?: string;
  aging: DebtAging;
}

export interface DebtorsReport {
  rows: DebtorRow[];
  totalOverdue: number;
  totalDebt: number;
  aging: DebtAging;
  debtorsCount: number;
}

export interface DebtorsReportApiResponse {
  success: boolean;
  code: string;
  reason: string;
  requestResult: DebtorsReport;
}

export interface AccountMovementDto {
  id: string;
  date: string;
  type: string; // 'Invoice', 'Payment', 'Reversal'
  document: string;
  description: string;
  debit: number;
  credit: number;
  status: string;
  reversed?: boolean; // Para indicar si una factura ha sido revertida
  paymentMethodName?: string; // Nombre de la forma de pago (cuando corresponda)
  paymentMethod?: string; // Código o nombre alternativo de la forma de pago
  invoiceType?: string; // 'Cash', 'CurrentAccount' o null (solo en movimientos de factura)
  effectiveAmount?: number; // Monto vigente con recargo del tramo activo
  baseAmount?: number; // Total base sin recargo (tooltip)
  activeDueDate?: string;
  dueDateNumber?: number;
  surchargePercent?: number;
}

export interface CustomerBasicInfo {
  name: string;
  document: string;
}

export interface AccountMovementsResponse {
  openingBalance: number;
  movements: AccountMovementDto[];
  customer: CustomerBasicInfo;
  totalDebit: number;
  totalCredit: number;
  finalBalance: number;
  overdueAmount: number;
  pendingDebt?: number;
  creditInFavor?: number;
  lastPaymentDate?: string;
  nextDueDate?: string;
  effectiveToday?: string;
}

export interface AccountMovementsApiResponse {
  success: boolean;
  code: string;
  reason: string;
  requestResult: AccountMovementsResponse;
}

export interface Customer {
  id?: string;
  firstName: string;
  surname: string;
  docType: string;
  docNumber: string;
  cuit: string;
  email: string;
  phoneNumber: string;
  taxCategoryId: string;
  taxCategoryCode?: string;
  taxCategoryName?: string;
  notes?: string;
  isDeleted?: boolean;
  vehicles?: CustomerVehicle[];
  subscriptions?: CustomerAbono[];
}

export interface CustomerResponse {
  success: boolean;
  code: string;
  reason: string;
  requestResult: Customer;
}

export interface CustomersResponse {
  success: boolean;
  code: string;
  reason: string;
  requestResult: Customer[];
}

export interface PaginatedCustomersResponse {
  success: boolean;
  code: string;
  reason: string;
  requestResult: {
    items: Customer[];
    totalCount: number;
    page: number;
    pageSize: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class CustomersService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiBaseUrl;

  // Headers de Authorization y X-Tenant-Id se agregan automáticamente por el interceptor

  getCustomer(id: string, includeDeletedSubscriptions: boolean = false): Observable<CustomerResponse> {
    const params = new HttpParams().set('includeDeletedSubscriptions', includeDeletedSubscriptions.toString());
    
    return this.http.get<CustomerResponse>(`${this.apiUrl}/api/v1/Customers/${id}`, {
      params
    });
  }

  getCustomers(): Observable<CustomersResponse> {
    return this.http.get<CustomersResponse>(`${this.apiUrl}/api/v1/Customers`);
  }

  getCustomerById(customerId: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/api/v1/Customers/${customerId}`);
  }

  getCustomersPaged(page: number, pageSize: number, search: string = ''): Observable<PaginatedCustomersResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());
    
    if (search.trim()) {
      params = params.set('search', search.trim());
    }

    return this.http.get<PaginatedCustomersResponse>(`${this.apiUrl}/api/v1/Customers/paged`, {
      params
    });
  }

  addCustomer(firstName: string, surname: string, docType: string, docNumber: string, cuit: string, email: string, phoneNumber: string, taxCategoryId: string, notes: string): Observable<any> {
    const body = {
      firstName,
      surname,
      docType,
      docNumber,
      cuit,
      email,
      phoneNumber,
      taxCategoryId,
      notes
    };
    
    return this.http.post(`${this.apiUrl}/api/v1/Customers`, body);
  }

  // Nuevo método para crear cliente básico (solo datos personales)
  addBasicCustomer(firstName: string, surname: string, docType: string, docNumber: string, cuit: string, email: string, phoneNumber: string, taxCategoryId: string, notes: string): Observable<any> {
    const body = {
      firstName,
      surname,
      docType,
      docNumber,
      cuit,
      email,
      phoneNumber,
      taxCategoryId,
      notes
    };
    
    return this.http.post(`${this.apiUrl}/api/v1/Customers/basic`, body);
  }

  // Método para crear customer con vehículos y abonos alineado con AddCustomerCommand
  addCustomerWithAbonos(customerData: {
    firstName: string;
    surname: string;
    docType: string;
    docNumber: string;
    cuit: string;
    email: string;
    phoneNumber: string;
    vehicles: {
      plate: string;
          }[];
    subscriptions: {
      vehicleIds?: string[] | null;
      plates?: string[] | null;
      planId?: string | null;
      assignedSpaceId?: string | null;
      paidThroughDate: string; // DateTime
      graceDays?: number;
      status?: string | null;
      price?: number | null;
      billingAnchorDate?: string | null; // DateOnly? 
      nextInvoiceDate?: string | null; // DateOnly?
      endOfMonthAligned?: boolean | null;
    }[];
  }): Observable<any> {
    return this.http.post(`${this.apiUrl}/api/v1/Customers`, customerData);
  }

  updateCustomer(id: string, firstName: string, surname: string, docType: string, docNumber: string, cuit: string, email: string, phoneNumber: string, taxCategoryId: string, notes: string): Observable<any> {
    const body = {
      firstName,
      surname,
      docType,
      docNumber,
      cuit,
      email,
      phoneNumber,
      taxCategoryId,
      notes
    };
    
    return this.http.put(`${this.apiUrl}/api/v1/Customers/${id}`, body);
  }

  // Nuevo método para actualizar cliente básico (solo datos personales)
  updateBasicCustomer(id: string, firstName: string, surname: string, docType: string, docNumber: string, cuit: string, email: string, phoneNumber: string, taxCategoryId: string, notes: string): Observable<any> {
    const body = {
      firstName,
      surname,
      docType,
      docNumber,
      cuit,
      email,
      phoneNumber,
      taxCategoryId,
      notes
    };
    
    return this.http.put(`${this.apiUrl}/api/v1/Customers/${id}/basic`, body);
  }

  // Método para actualizar customer con abonos completos alineado con UpdateCustomerCommand
  updateCustomerWithAbonos(id: string, customerData: {
    id: string;
    firstName: string;
    surname: string;
    docType: string;
    docNumber: string;
    cuit: string;
    email: string;
    phoneNumber: string;
    vehicles: {
      plate: string;
          }[];
    subscriptions: {
      id?: string | null; // id del abono existente (null = nuevo)
      vehicleIds?: string[] | null;
      plates?: string[] | null;
      planId?: string | null;
      spaceId?: string | null; // Note: spaceId not assignedSpaceId in UpdateCommand
      paidThroughDate?: string | null; // DateTime?
      graceDays?: number | null; // byte?
      status?: string | null;
      price?: number | null; // decimal?
      isDeleted?: boolean | null;
      billingAnchorDate?: string | null; // DateOnly?
      nextInvoiceDate?: string | null; // DateOnly?
      endOfMonthAligned?: boolean | null;
    }[];
  }): Observable<any> {
    return this.http.put(`${this.apiUrl}/api/v1/Customers/${id}`, customerData);
  }

  estimateAbonoProration(abonoId: string): Observable<SubscriptionProrationEstimateResponse> {
    return this.http.get<SubscriptionProrationEstimateResponse>(`${this.apiUrl}/api/v1/Subscriptions/${abonoId}/proration`);
  }

  generateRestOfMonthInvoice(request: CreateSubscriptionInvoiceRequest): Observable<InvoiceResponse> {
    return this.http.post<InvoiceResponse>(`${this.apiUrl}/api/v1/Invoices/subscription`, request);
  }

  deleteCustomer(id: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/api/v1/Customers/${id}`);
  }

  // Método para obtener vehículos de un cliente
  getCustomerVehicles(customerId: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/api/v1/Customers/${customerId}/vehicles`);
  }

  // Método para actualizar vehículos de un cliente
  updateCustomerVehicles(customerId: string, vehicles: {
    id?: string;
    plate: string;
    description?: string;
    notes?: string;
    isDeleted?: boolean;
  }[]): Observable<any> {
    const body = {
      vehicles: vehicles.map(v => ({
        id: v.id || null,
        plate: v.plate,
        description: v.description || '',
        notes: v.notes || '',
        isDeleted: v.isDeleted || false
      }))
    };
    
    return this.http.put(`${this.apiUrl}/api/v1/Customers/${customerId}/vehicles`, body);
  }

  // Método para obtener abonos de un cliente
  getCustomerAbonos(customerId: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/api/v1/Customers/${customerId}/subscriptions`);
  }

  // Método para actualizar abonos de un cliente
  updateCustomerAbonos(customerId: string, abonos: {
    id?: string;
    planId: string;
    vehiclePlates: string[];
    assignedSpaceId?: string | null;
    graceDays: number;
    price: number;
    billingAnchorDate?: string;
    endOfMonthAligned: boolean;
    isDeleted?: boolean;
  }[]): Observable<any> {
    const body = {
      subscriptions: abonos.map(s => ({
        id: s.id || null,
        planId: s.planId,
        vehiclePlates: s.vehiclePlates,
        plates: s.vehiclePlates,
        assignedSpaceId: s.assignedSpaceId || null,
        graceDays: s.graceDays || 0,
        price: s.price || 0,
        billingAnchorDate: s.billingAnchorDate || new Date().toISOString().split('T')[0],
        endOfMonthAligned: true,
        isDeleted: s.isDeleted || false
      }))
    };
    
    return this.http.put(`${this.apiUrl}/api/v1/Customers/${customerId}/subscriptions`, body);
  }

  // Método para otorgar período de cortesía a un abono
  grantSubscriptionCourtesyPeriod(subscriptionId: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/api/v1/Subscriptions/${subscriptionId}/courtesy-period`, {});
  }

  // ─── Interfaces y método para el reporte de deudores ───────────────────────

  getDebtorsReport(minAmount?: number, sortBy?: string): Observable<DebtorsReportApiResponse> {
    let params = new HttpParams();
    if (minAmount != null) params = params.set('minAmount', minAmount.toString());
    if (sortBy) params = params.set('sortBy', sortBy);
    return this.http.get<DebtorsReportApiResponse>(`${this.apiUrl}/api/v1/financial-reports/debtors`, { params });
  }

  // Método para obtener movimientos de cuenta corriente
  getCustomerAccountMovements(
    customerId: string, 
    fromDate?: string, 
    toDate?: string
  ): Observable<AccountMovementsApiResponse> {
    let params = new HttpParams();
    if (fromDate) {
      params = params.set('fromDate', fromDate);
    }
    if (toDate) {
      params = params.set('toDate', toDate);
    }

    return this.http.get<AccountMovementsApiResponse>(
      `${this.apiUrl}/api/v1/Customers/${customerId}/account-movements`, 
      {
        params
      }
    );
  }
}



